import React, { useEffect, useLayoutEffect, useState } from "react";
import { Typography, Paper, Grid2, Pagination } from "@mui/material";
import { useNavigate } from "react-router-dom";
import AxiosInstance from "../utils/Axios.ts";
import DynamicTab from "../components/extras/DynamicTab.tsx";
import ParentCard from "../components/ParentComponents/ParentCard.tsx";
import ParentFiltersPanel, { ParentListFilters } from "../components/ParentComponents/ParentFiltersPanel.tsx";
import ParentTourListItemButton from "../components/ParentComponents/ParentTourListItemButton.tsx";
import LoadingSpinner from "../components/basics/LoadingSpinner.tsx";
import ErrorMessage from "../components/basics/ErrorMessage.tsx";

interface Parent {
  id: number;
  name: string;
  location_name: string;
  date: string;
  time_from: string;
  time_to: string;
  booking_id: string;
  agent_name: string;
  tourType_name: string;
}

function formatLocalYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const ParentPage: React.FC = () => {
  const [parents, setParents] = useState<Parent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<ParentListFilters>({
    name: "",
    booking_id: "",
    date_from: "",
    date_to: "",
    location: null,
    agent: null,
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
  });
  const navigate = useNavigate();

  /* Refresh / bfcache can restore scroll mid-page; bar used to hide on scroll — keep top in view */
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchParents = async (page = 1, currentFilters = filters) => {
    try {
      // Create a new object with only non-null and non-empty values
      const filterParams: Record<string, string> = {};

      // Add page parameter
      filterParams.page = page.toString();

      // Add other filter parameters only if they have values
      if (currentFilters.name) filterParams.name = currentFilters.name;
      if (currentFilters.booking_id) filterParams.booking_id = currentFilters.booking_id;
      if (currentFilters.date_from) filterParams.date_from = currentFilters.date_from;
      if (currentFilters.date_to) filterParams.date_to = currentFilters.date_to;
      if (currentFilters.location) filterParams.location = currentFilters.location.toString();
      if (currentFilters.agent) filterParams.agent = currentFilters.agent.toString();

      const params = new URLSearchParams(filterParams);

      const response = await AxiosInstance.get(`/parent/?${params.toString()}`);
      if (response.status === 200) {
        const results = response.data || [];
        setParents(results.results);
        setPagination({
          currentPage: page,
          totalPages: Math.ceil(results.count / 20), // Assuming 20 items per page
        });
      } else {
        throw new Error("Unexpected response status.");
      }
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        localStorage.clear();
        navigate("/login");
      } else {
        setError(error.message || "Something went wrong.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchParents(1, filters);
    // eslint-disable-next-line
  }, [filters]);

  const handleDateFieldChange = (field: "date_from" | "date_to", value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      name: "",
      booking_id: "",
      date_from: "",
      date_to: "",
      location: null,
      agent: null,
    });
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    fetchParents(value, filters);
  };

  const handleLocationChange = (value: { id: number } | null) => {
    setFilters((prev) => ({ ...prev, location: value?.id || null }));
  };

  const handleAgentChange = (value: number | null) => {
    setFilters((prev) => ({ ...prev, agent: value && value > 0 ? value : null }));
  };

  const normalizeDateRange = () => {
    setFilters((prev) => {
      if (!prev.date_from || !prev.date_to || prev.date_from <= prev.date_to) return prev;
      return { ...prev, date_from: prev.date_to, date_to: prev.date_from };
    });
  };

  const handleNextTwoWeeks = () => {
    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + 13);
    setFilters((prev) => ({
      ...prev,
      date_from: formatLocalYMD(start),
      date_to: formatLocalYMD(end),
    }));
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <DynamicTab title="IT Master" favicon="/logos/IT logo.png">
      <Grid2
        container
        spacing={2}
        direction="column"
        sx={{
          width: "100%",
          maxWidth: "100%",
          alignItems: "stretch",
          margin: 0,
        }}
      >
        <ParentFiltersPanel
          filters={filters}
          onNameChange={(v) => setFilters((p) => ({ ...p, name: v }))}
          onLocationChange={handleLocationChange}
          onAgentChange={handleAgentChange}
          onDateFieldChange={handleDateFieldChange}
          onNormalizeDateRange={normalizeDateRange}
          onNextTwoWeeks={handleNextTwoWeeks}
          onReset={handleResetFilters}
        />

        <Paper
          sx={{
            p: 0,
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            borderRadius: 2,
          }}
        >
          <Grid2 container spacing={2} direction="column" alignItems="stretch">
            {parents.length > 0 ? (
              parents.map((parent) => (
                <Grid2
                  sx={{
                    width: "100%",
                  }}
                  key={parent.id}
                >
                  <ParentTourListItemButton parentId={parent.id}>
                    <ParentCard
                      id={parent.id}
                      name={parent.name}
                      location={parent.location_name || "Unknown Location"}
                      dateFrom={parent.date || "N/A"}
                      dateTo={parent.date || "N/A"}
                      timeFrom={parent.time_from || "N/A"}
                      timeTo={parent.time_to || "N/A"}
                      bookingNum={parent.booking_id || "N/A"}
                      agentName={parent.agent_name || "Unknown Agent"}
                      tourType={parent.tourType_name || "Unknown Tour Type"}
                    />
                  </ParentTourListItemButton>
                </Grid2>
              ))
            ) : (
              <Typography
                variant="h6"
                style={{ textAlign: "center", padding: "2%" }}
              >
                No parent tours available.
              </Typography>
            )}
          </Grid2>
          <Pagination
            count={pagination.totalPages}
            page={pagination.currentPage}
            onChange={handlePageChange}
            color="primary"
            style={{ marginTop: "20px", marginBottom: "20px" }}
          />
        </Paper>
      </Grid2>
    </DynamicTab>
  );
};

export default ParentPage;
